import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  SpinnerGap,
  Receipt,
  CheckCircle,
  XCircle,
  Clock,
  CaretRight,
  MagnifyingGlass,
  FileText,
  Funnel,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminBillingLogs() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("transactions");
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [txnStatusFilter, setTxnStatusFilter] = useState("all");
  const [txnTenantFilter, setTxnTenantFilter] = useState("");
  
  // Logs state
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [logEventFilter, setLogEventFilter] = useState("all");
  const [logResultFilter, setLogResultFilter] = useState("all");
  
  // Detail modal
  const [transactionDetailModal, setTransactionDetailModal] = useState(null);

  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    } else {
      fetchLogs();
    }
  }, [activeTab, txnStatusFilter, txnTenantFilter, logEventFilter, logResultFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (txnStatusFilter !== "all") params.append("status", txnStatusFilter);
      if (txnTenantFilter) params.append("tenant_id", txnTenantFilter);
      
      const response = await fetch(
        `${API}/admin/billing/transactions?${params.toString()}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setTotalTransactions(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logEventFilter !== "all") params.append("event_type", logEventFilter);
      if (logResultFilter !== "all") params.append("result", logResultFilter);
      
      const response = await fetch(
        `${API}/admin/billing/logs?${params.toString()}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalLogs(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionDetail = async (transactionId) => {
    try {
      const response = await fetch(
        `${API}/admin/billing/transactions/${transactionId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setTransactionDetailModal(data);
      }
    } catch (error) {
      toast.error("Failed to load transaction details");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "success":
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={14} className="mr-1" />Success</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700"><XCircle size={14} className="mr-1" />Failed</Badge>;
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700"><Clock size={14} className="mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getResultBadge = (result) => {
    switch (result) {
      case "success":
        return <Badge className="bg-emerald-100 text-emerald-700">Success</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
      default:
        return <Badge variant="outline">{result}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-billing-logs">
      <div>
        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
          Billing Administration
        </h1>
        <p className="text-zinc-500">View and manage billing transactions and logs</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <Receipt size={16} />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText size={16} />
            Activity Logs
          </TabsTrigger>
        </TabsList>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-4">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4 p-4 bg-zinc-50 rounded-sm">
            <Funnel size={20} className="text-zinc-500" />
            <div className="flex-1 flex gap-4">
              <div className="w-[180px]">
                <Select value={txnStatusFilter} onValueChange={setTxnStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="success">Successful</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 max-w-[300px]">
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    placeholder="Filter by Tenant ID..."
                    value={txnTenantFilter}
                    onChange={(e) => setTxnTenantFilter(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={fetchTransactions} size="sm">
              Apply
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
            </div>
          ) : transactions.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="p-8 text-center">
                <Receipt size={48} className="mx-auto text-zinc-300 mb-4" />
                <p className="text-zinc-500">No transactions found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Date</th>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Company</th>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Description</th>
                        <th className="text-right p-3 text-sm font-medium text-zinc-600">Amount</th>
                        <th className="text-center p-3 text-sm font-medium text-zinc-600">Status</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {transactions.map((txn) => (
                        <tr key={txn.transaction_id} className="hover:bg-zinc-50">
                          <td className="p-3 text-sm">{formatDate(txn.datetime)}</td>
                          <td className="p-3 text-sm font-medium">{txn.company_name || "—"}</td>
                          <td className="p-3 text-sm text-zinc-600 max-w-[200px] truncate">
                            {txn.description}
                          </td>
                          <td className="p-3 text-sm text-right font-medium">{txn.amount_display}</td>
                          <td className="p-3 text-center">{getStatusBadge(txn.status)}</td>
                          <td className="p-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchTransactionDetail(txn.transaction_id)}
                            >
                              <CaretRight size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          
          <p className="text-center text-sm text-zinc-500 mt-4">
            Showing {transactions.length} of {totalTransactions} transactions
          </p>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="mt-4">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4 p-4 bg-zinc-50 rounded-sm">
            <Funnel size={20} className="text-zinc-500" />
            <div className="flex-1 flex gap-4">
              <div className="w-[180px]">
                <Select value={logEventFilter} onValueChange={setLogEventFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Event Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="card_stored">Card Stored</SelectItem>
                    <SelectItem value="card_store_failed">Card Store Failed</SelectItem>
                    <SelectItem value="charge_success">Charge Success</SelectItem>
                    <SelectItem value="charge_failed">Charge Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[180px]">
                <Select value={logResultFilter} onValueChange={setLogResultFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Result" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Results</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button variant="outline" onClick={fetchLogs} size="sm">
              Apply
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
            </div>
          ) : logs.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="p-8 text-center">
                <FileText size={48} className="mx-auto text-zinc-300 mb-4" />
                <p className="text-zinc-500">No logs found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Timestamp</th>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Tenant ID</th>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Event</th>
                        <th className="text-center p-3 text-sm font-medium text-zinc-600">Result</th>
                        <th className="text-left p-3 text-sm font-medium text-zinc-600">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {logs.map((log, idx) => (
                        <tr key={log.log_id || idx} className="hover:bg-zinc-50">
                          <td className="p-3 text-sm font-mono">{formatDate(log.timestamp)}</td>
                          <td className="p-3 text-sm font-mono text-zinc-600">{log.tenant_id?.slice(-8) || "—"}</td>
                          <td className="p-3 text-sm">
                            <Badge variant="outline">{log.event_type}</Badge>
                          </td>
                          <td className="p-3 text-center">{getResultBadge(log.result)}</td>
                          <td className="p-3 text-sm text-zinc-600 max-w-[300px]">
                            <code className="text-xs bg-zinc-100 px-2 py-1 rounded">
                              {JSON.stringify(log.response_summary || {}).slice(0, 50)}...
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          
          <p className="text-center text-sm text-zinc-500 mt-4">
            Showing {logs.length} of {totalLogs} logs
          </p>
        </TabsContent>
      </Tabs>

      {/* Transaction Detail Modal */}
      <Dialog open={!!transactionDetailModal} onOpenChange={() => setTransactionDetailModal(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Transaction Details</DialogTitle>
          </DialogHeader>
          
          {transactionDetailModal && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-sm">
                <span className="text-zinc-600">Amount</span>
                <span className="text-2xl font-bold">{transactionDetailModal.transaction?.amount_display}</span>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Status</span>
                  {getStatusBadge(transactionDetailModal.transaction?.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Company</span>
                  <span>{transactionDetailModal.transaction?.company_name || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Tenant ID</span>
                  <span className="font-mono">{transactionDetailModal.transaction?.tenant_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Date</span>
                  <span>{formatDate(transactionDetailModal.transaction?.datetime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Description</span>
                  <span className="text-right max-w-[200px]">{transactionDetailModal.transaction?.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Transaction ID</span>
                  <span className="font-mono text-xs">{transactionDetailModal.transaction?.transaction_id}</span>
                </div>
                {transactionDetailModal.transaction?.tc_transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">TC Reference</span>
                    <span className="font-mono">{transactionDetailModal.transaction?.tc_transaction_id}</span>
                  </div>
                )}
                {transactionDetailModal.transaction?.tc_auth_code && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Auth Code</span>
                    <span className="font-mono">{transactionDetailModal.transaction?.tc_auth_code}</span>
                  </div>
                )}
                {transactionDetailModal.transaction?.tc_response_message && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Response</span>
                    <span>{transactionDetailModal.transaction?.tc_response_message}</span>
                  </div>
                )}
                {transactionDetailModal.transaction?.payment_method_snapshot && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Card</span>
                    <span className="capitalize">
                      {transactionDetailModal.transaction.payment_method_snapshot.brand} •••• {transactionDetailModal.transaction.payment_method_snapshot.last4}
                    </span>
                  </div>
                )}
              </div>
              
              {transactionDetailModal.logs?.length > 0 && (
                <div className="pt-4 border-t border-zinc-200">
                  <h4 className="font-medium mb-3">Activity Log</h4>
                  <div className="space-y-2 text-sm max-h-40 overflow-y-auto">
                    {transactionDetailModal.logs.map((log, idx) => (
                      <div key={idx} className="p-2 bg-zinc-50 rounded text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{log.event_type}</span>
                          <span className="text-zinc-400">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                        <code className="text-zinc-600">
                          {JSON.stringify(log.response_summary || {})}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <Button variant="outline" onClick={() => setTransactionDetailModal(null)} className="w-full border-2">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
